import { JSX } from 'solid-js';
import clsx from 'clsx';

// TODO: Rename when all touched files have been updated. Current name is legacy from styled components era. 

/**
 * Single-wrapper layout for the in-page sidebar menu. Replaces the old
 * SidebarContainer > SidebarContainerLine > Sidebar > SidebarInner stack.
 * Provides the <nav> landmark; caller supplies the menu tree.
 */
export const SidebarPanel = (props: { children?: JSX.Element; class?: string }) => (
    <nav
        class={clsx(
            // Hidden below tablet (mobile sidebar is a separate dropdown).
            'max-tablet-original:hidden',
            // Sticky column beneath the header — flows with page scroll (no internal scroll container).
            // Header height is measured (header.tsx) — it varies by breakpoint and
            // collapse state, so a fixed offset clipped the first item on desktop.
            '[container-type:inline-size] sticky top-[calc(var(--header-height,6.43rem)+var(--spacing-cvi-6))]',
            // No top-padding — sidebar pages get page-level top-spacing from the parent wrapper.
            // The first content Section's top-padding is stripped on sidebar pages (see Phase A/D plan) so
            // sidebar + content align flush at the top of the grid.
            // No margin-right: `.sidebar-page-layout` owns the rail-to-content gap.
            'pb-cvi-10',
            props.class
        )}
    >
        {props.children}
    </nav>
);

/** @deprecated — prefer `SidebarPanel` for new code. Kept for products / files / events consumers still on the 4-wrapper stack. */
export const SidebarContainer = (props: { children?: JSX.Element; class?: string; ref?: any }) => (
    <div
        ref={props.ref}
        class={clsx('h-full z-[1] max-tablet:pr-0', props.class)}
    >
        {props.children}
    </div>
);

/** @deprecated — prefer `SidebarPanel`. Renders a right-side blue divider; not in the new DS design. */
export const SidebarContainerLine = (props: { children?: JSX.Element; class?: string; className?: string }) => (
    <div
        class={clsx('h-full border-r-[2px] border-r-blue-300 max-tablet:border-r-0', props.class, props.className)}
    >
        {props.children}
    </div>
);

/** @deprecated — prefer `SidebarPanel`. */
export const SidebarInner = (props: { children?: JSX.Element; class?: string }) => (
    <nav
        class={`min-[600px]:pt-[6.43rem] ${props.class || ''}`}
    >
        {props.children}
    </nav>
);

/** @deprecated — prefer `SidebarPanel`. */
export const Sidebar = (props: { children?: JSX.Element; class?: string; ref?: any }) => (
    <div
        ref={props.ref}
        class={clsx(
            '[container-type:inline-size] h-auto sticky top-[6.43rem] pb-[4rem] mr-[3rem]',
            'max-desktop:pt-[3rem] max-desktop:mr-[1.5rem]',
            'max-[1024px]:mr-[1rem]',
            'min-[600px]:h-[calc(100vh-6.43rem)] min-[600px]:overflow-y-auto min-[600px]:[&::-webkit-scrollbar]:hidden min-[600px]:[-ms-overflow-style:none] min-[600px]:[scrollbar-width:none]',
            'max-tablet:static max-tablet:px-0 max-tablet:mr-0 max-tablet:pb-0 max-tablet:z-[2]',
            props.class
        )}
    >
        {props.children}
    </div>
);

export const gridSettings = {
    container: {
        templateShorthand: [3, 9],
        responsive: {
            largeDesktop: [4, 8],
            smallDesktop: [4, 8],
            tablet: [12],
            mobile: [12],
        }
    },
    sidebar: {
        inheritParentGrid: true,
        templateShorthand: [3],
        responsive: {
            largeDesktop: [4],
            smallDesktop: [4],
            tablet: [1],
            mobile: [1],
        },
        customCss: `
            height: 100%;
        `
    },
    listing: {
        inheritParentGrid: true,
        templateShorthand: [3, 3, 3],
        responsive: {
            largeDesktop: [4, 4],
            smallDesktop: [4, 4],
            tablet: [12],
            mobile: [12],
        }
    }
};
